import { env } from "@config/env";
import {
  clearSelectedCompany,
  syncSelectedCompanyFromStorage,
} from "@shared/session/companySelection";
import { useSyncExternalStore } from "react";
import type { LoginSession } from "../types";
import { apiRequest } from "@/shared";

const SESSION_STORAGE_KEY = "vccipl.auth.session";

/** Legacy flat keys written alongside the auth session blob. */
const AUTH_SESSION_KEYS = [
  SESSION_STORAGE_KEY,
  "UserType",
  "CompanyId",
  "CompanyName",
  "SessionId",
  "UserId",
  "UserName",
  "SelectedCompanyIds",
  "BenposDate",
] as const;

type AuthSessionListener = () => void;
const authSessionListeners = new Set<AuthSessionListener>();

/** Stable snapshot for useSyncExternalStore (must not be a new object every call). */
let cachedRaw: string | null | undefined;
let cachedSession: LoginSession | null = null;

function notifyAuthSessionChanged(): void {
  authSessionListeners.forEach((listener) => listener());
}

export function subscribeAuthSession(listener: AuthSessionListener): () => void {
  authSessionListeners.add(listener);
  return () => authSessionListeners.delete(listener);
}

function syncCacheFromStorage(): LoginSession | null {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);

  if (raw === cachedRaw) {
    return cachedSession;
  }

  cachedRaw = raw;

  if (!raw) {
    cachedSession = null;
    return null;
  }

  try {
    cachedSession = JSON.parse(raw) as LoginSession;
  } catch {
    cachedSession = null;
  }

  return cachedSession;
}

function getAuthSessionSnapshot(): LoginSession | null {
  return syncCacheFromStorage();
}

/** Re-read session from storage when login/logout updates localStorage. */
export function useAuthSession(): LoginSession | null {
  return useSyncExternalStore(subscribeAuthSession, getAuthSessionSnapshot, () => null);
}

export function saveAuthSession(session: LoginSession): void {
  debugger;
  const raw = JSON.stringify(session);
  localStorage.setItem(SESSION_STORAGE_KEY, raw);
  localStorage.setItem("UserType", session.user.userType);
  localStorage.setItem("CompanyId", session.user.companyId);
  localStorage.setItem("CompanyName", session.user.companyName);
  localStorage.setItem("SessionId", session.sessionId);
  localStorage.setItem("UserId", session.user.userId);
  localStorage.setItem("UserName", session.user.userName);
  localStorage.setItem("BenposDate", session.user.benposDate);
  localStorage.setItem("ListedCompany", session.user.listedCompany);
  localStorage.setItem("SelectedCompanyIds", session.user.selectedCompanyIds);

  syncSelectedCompanyFromStorage();

  cachedRaw = raw;
  cachedSession = session;
  notifyAuthSessionChanged();
}

export function getAuthSession(): LoginSession | null {
  return syncCacheFromStorage();
}

export function clearAuthSession(options?: { notifyServer?: boolean }): void {
  for (const key of AUTH_SESSION_KEYS) {
    localStorage.removeItem(key);
  }

  clearSelectedCompany();

  cachedRaw = null;
  cachedSession = null;
  notifyAuthSessionChanged();

  if (options?.notifyServer !== false && typeof window !== "undefined") {
    const logoutUrl = `${env.apiBaseUrl || ""}/api/auth/logout`;
    void apiRequest(logoutUrl, { method: "POST", data: "include" }).catch(() => {});
  }
}

export function isAuthenticated(): boolean {
  return getAuthSession() !== null;
}
